"use client";
import { useTranslations } from "next-intl";
import { Skeleton } from "@radix-ui/themes";
import { DataInputParams } from "./inputType";
import { Form, Button, Slider } from "antd";
import { checkDisabled, checkNoItem, GetDataInputElement, UnitConvertor } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";
import { getInput as getInputNumber } from "./inputNumber";
import { Debug } from "@/util/debug";
import { JSX, ReactNode, useEffect, useState } from "react";

// import dynamic from "next/dynamic";
// const Slider = dynamic(() => import("antd").then((mod) => mod.Slider), { ssr: false });

const getMark = (value: number, unitPrev: string | JSX.Element | null | undefined, unit: string | JSX.Element | null | undefined) => {
  const mergePrev =
    typeof unitPrev === "string" || unitPrev === undefined ? (
      (unitPrev ?? "") + value
    ) : (
      <>
        {unitPrev}
        {value}
      </>
    );
  const mergeNext =
    typeof unit === "string" || unit === undefined ? (
      mergePrev + (unit ?? "")
    ) : (
      <>
        {mergePrev} {unit}
      </>
    );
  return mergeNext;
};
type MarkObj = {
  style?: React.CSSProperties;
  label?: React.ReactNode;
};
export const getInputSlider = (t: TransitionType, params: DataInputParams) => {
  //Debug(params);
  //const disabled = params.inputMode == "read" ? true : params.disabled;
  const disabled = checkDisabled(params);
  const value = params.inputMode == "view" && !(params.readInputDisabled ?? false) ? params.value : undefined;
  const step = typeof params.step === "number" ? params.step : params.scale ? 1 / params.scale : undefined;
  const minValue = params.minValue !== undefined ? Number(params.minValue) : 0;
  const maxValue = params.maxValue !== undefined ? Number(params.maxValue) : 100;
  const marks: Record<string | number, ReactNode | MarkObj> | undefined =
    params.sliderProp?.marks === undefined
      ? params.sliderProp?.autoMark || params.sliderProp?.valueMark
        ? {}
        : undefined
      : { ...params.sliderProp.marks };
  if (params.sliderProp?.autoMark && marks) {
    const unitPrev = params.unitPrev ? GetDataInputElement(params.unitPrev, false, params.values, t) : undefined;
    const unit = params.unit ? GetDataInputElement(params.unit, true, params.values, t) : undefined;
    marks[minValue] = getMark(minValue, unitPrev, unit);
    marks[maxValue] = getMark(maxValue, unitPrev, unit);
  }
  if (marks && params.sliderProp?.valueMark && params.value) {
    const check_params_value = typeof params.value === "number" && params.value < 0 ? params.value + "" : params.value;
    // Debug(marks, check_params_value);
    if (!marks[check_params_value] || (marks && params.sliderProp?.valueOverride)) {
      const unitPrev = params.unitPrev ? GetDataInputElement(params.unitPrev, false, params.values, t) : undefined;
      const unit = params.unit ? GetDataInputElement(params.unit, true, params.values, t) : undefined;
      const label =
        typeof params.sliderProp.valueMark === "string"
          ? params.sliderProp.valueMark
          : typeof params.sliderProp.valueMark === "boolean"
            ? getMark(params.value, unitPrev, unit)
            : GetDataInputElement(params.sliderProp.valueMark, false, params.values, t);
      marks[check_params_value] = { label, style: params.sliderProp.valueStyle };
    }
  }
  return (
    <Slider
      className={params.inputClassName ?? "w-full"}
      style={params.inputStyle}
      value={value}
      //defaultValue={params.value}
      disabled={disabled}
      id={params.name}
      min={params.minValue !== undefined ? Number(params.minValue) : 0}
      max={params.maxValue !== undefined ? Number(params.maxValue) : 100}
      step={step}
      // onChangeComplete={params.onChangeComplete}
      {...params.sliderProp}
      marks={marks}
      onChange={(e: any) => {
        if (params.form) {
          // params.form.setFieldValue(params.name, e);
          params.form.setFieldValue(params.name + "_inputNumber", e);
        }
        if (params.onChange) {
          params.onChange({ key: params.name, value: e, index: params.index, name: params.subListName });
        }
      }}
    />
  );
};

export default function DataInputSlider(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const inputPrev = params.inputPrev ? GetDataInputElement(params.inputPrev, false, params.values, t) : undefined;
  const inputNext = params.inputNext ? GetDataInputElement(params.inputNext, true, params.values, t) : undefined;
  const minValue = params.minValue !== undefined ? Number(params.minValue) : 0;
  const maxValue = params.maxValue !== undefined ? Number(params.maxValue) : 100;
  const [unit, setUnit] = useState(params.unit);
  const [step, setStep] = useState(params.step ?? (params.scale ? 1 / params.scale : undefined));
  const [scale, setScale] = useState(params.scale);
  const [sync, setSync] = useState(false);

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    params.formFunction[params.name]["setUnit"] = setUnit;
    params.formFunction[params.name]["setStep"] = setStep;
    params.formFunction[params.name]["inputSync"] = () => {
      if (params.form) {
        if (!sync) {
          setSync(true);
          const value = params.form.getFieldValue(params.name);
          params.form.setFieldValue(params.name + "_inputNumber", value);
          setTimeout(() => setSync(false), 100);
        }
      }
    };
  }

  useEffect(() => {
    if (params.formMemo && params.name && params.unit) {
      if (!params.formMemo[params.name]) {
        params.formMemo[params.name] = {};
      }
      const temp_memo = params.formMemo[params.name];
      if (temp_memo) {
        const beforeUnit = temp_memo.convertUnit ?? params.unit;
        const beforeScale = temp_memo.convertScale ?? params.scale;
        const beforeStep = temp_memo.convertStep ?? params.step ?? (params.scale ? 1 / params.scale : undefined);
        params.formMemo[params.name] = {
          ...params.formMemo[params.name],
          beforeUnit,
          beforeScale,
          beforeStep,
          convertUnit: unit,
          convertScale: beforeScale,
          convertStep: beforeStep,
        };
        UnitConvertor(params);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.formMemo, params.name, params.unit, params.step, params.scale, unit]);

  const mergeParams = { ...params, minValue: minValue, maxValue: maxValue };
  const disabled = checkDisabled(params);

  const onChangeComplete = (e: any) => {
    if (params.form) {
      params.form.setFieldValue(params.name, e.value);
      // params.form.setFieldValue(params.name + "_inputNumber", e.value);
    }
    if (params.onChange) {
      params.onChange({ key: params.name, value: e.value, index: params.index, name: params.subListName });
    }
  };

  const minButton =
    !disabled && params.minButton ? (
      <Button
        {...params.minButton}
        onClick={
          minValue !== null && minValue !== undefined
            ? (e) => {
                params.minButton?.onClick ? params.minButton?.onClick(e) : undefined;
                onChangeComplete(minValue ? Number(minValue) : 0);
              }
            : undefined
        }
      />
    ) : undefined;
  const maxButton =
    !disabled && params.maxButton ? (
      <Button
        {...params.maxButton}
        onClick={
          maxValue !== null && maxValue !== undefined
            ? (e) => {
                params.maxButton?.onClick ? params.maxButton?.onClick(e) : undefined;
                onChangeComplete(maxValue);
              }
            : undefined
        }
      />
    ) : undefined;
  const inputNumber =
    params.inputType === "sliderInput"
      ? getInputNumber(t, {
          ...mergeParams,
          onChange: (e: any) => {
            if (params.form) {
              params.form.setFieldValue(params.name, e.value);
              // params.form.setFieldValue(params.name + "_inputNumber", e.value);
            }
            if (params.onChange) {
              params.onChange({ key: params.name, value: e.value, index: params.index, name: params.subListName });
            }
          },
          inputClassName: params.inputNumberClassName ?? "ml-3",
        })
      : "";
  return (
    <Skeleton loading={params.loading ?? false}>
      {prev}
      {checkNoItem(params) ? (
        <>
          {inputPrev}
          {minButton}
          {getInputSlider(t, params)}
          {maxButton}
          {inputNext}
          {inputNumber}
        </>
      ) : (
        <>
          {inputPrev}
          {params.sliderProp?.reverse ? maxButton : minButton}
          <Form.Item name={params.name} noStyle initialValue={params.value ?? params.defaultValue}>
            {getInputSlider(t, { ...mergeParams })}
          </Form.Item>
          {params.sliderProp?.reverse ? minButton : maxButton}
          {inputNext}
          <Form.Item
            name={params.name + "_inputNumber"}
            noStyle
            initialValue={params.value ?? params.defaultValue}
            required={params.required}
          >
            {inputNumber}
          </Form.Item>
        </>
      )}
      {next}
    </Skeleton>
  );
}
